import React, { useState, useEffect, useContext } from "react";
import { Toaster } from "../../../../common/Toaster/Toaster";
import { createPricingPlan } from "../../../../service/pricingPlanService";
import { ApiContext } from "../../../../context/MyContext";
import { getSubscriptionCommonUtils } from "../../../../service/SubscriptionWellDhanService";

const initialPricing = { duration: 1, price: 0, max_discount: 0, country: "", currency: "" };

const PricingListForm = ({
  planId,
  post,
  planName,
  onSuccess,
  onClose,
  initialList = [{ ...initialPricing }],
  submitLabel = "Save Pricing",
  headerText = "Add New Pricing Option",
}) => {
  const { get } = useContext(ApiContext);
  const [pricingList, setPricingList] = useState(initialList);
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");
  const [commonUtils, setCommonUtils] = useState(null);
  const [loadingUtils, setLoadingUtils] = useState(true);

  // Fetch commonUtils (countries and denominations)
  useEffect(() => {
    const fetchUtils = async () => {
      try {
        const result = await getSubscriptionCommonUtils(get);
        if (result.success) {
          setCommonUtils(result.data);
        } else {
          setToastMessage(
            result.message || "Failed to load configuration data"
          );
          setToastType("error");
        }
      } catch (err) {
        setToastMessage("Failed to load configuration data");
        setToastType("error");
      } finally {
        setLoadingUtils(false);
      }
    };
    fetchUtils();
  }, [get]);

  // Set default country and currency when commonUtils loads
  useEffect(() => {
    if (commonUtils?.countries?.length > 0 && commonUtils?.denominations?.length > 0) {
      setPricingList((prevList) => {
        // Only update if the first item doesn't have country/currency set
        if (prevList.length > 0 && (!prevList[0].country || !prevList[0].currency)) {
          const defaultCountry = commonUtils.countries.find(c => 
            c.name?.toLowerCase().includes("india")
          ) || commonUtils.countries[0];
          const defaultCurrency = commonUtils.denominations.find(d => 
            d.name?.toLowerCase().includes("rupee") || d.symbol === "INR"
          ) || commonUtils.denominations[0];
          
          return [{
            ...prevList[0],
            country: defaultCountry.name,
            currency: defaultCurrency.name
          }];
        }
        return prevList;
      });
    }
  }, [commonUtils]);

  useEffect(() => {
    if (toastMessage) {
      const timeout = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timeout);
    }
  }, [toastMessage]);

  // Get country_id by NAME from utils
  const getCountryId = (countryName) => {
    const found = commonUtils?.countries?.find(
      (c) => c.name?.toLowerCase() === countryName.toLowerCase()
    );
    return found?.id || null;
  };

  // Get denomination_id by NAME from utils
  const getDenominationId = (currencyName) => {
    const found = commonUtils?.denominations?.find(
      (d) =>
        d.name?.toLowerCase() === currencyName.toLowerCase() ||
        d.symbol?.toLowerCase() === currencyName.toLowerCase()
    );
    return found?.id || null;
  };

  const handlePricingChange = (idx, field, value) => {
    const updated = [...pricingList];
    updated[idx][field] = value;
    setPricingList(updated);
  };
  const addPricingOption = () => {
    const defaultCountry = commonUtils?.countries?.[0]?.name || "";
    const defaultCurrency = commonUtils?.denominations?.[0]?.name || "";
    setPricingList([...pricingList, { ...initialPricing, country: defaultCountry, currency: defaultCurrency }]);
  };
  const removePricingOption = (idx) => setPricingList(pricingList.filter((_, i) => i !== idx));

  const handlePricingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setToastMessage(null);
    if (!pricingList.length || pricingList.some(p => !p.duration || !p.price)) {
      setToastMessage("All pricing fields are required." );
      setToastType("error");
      setLoading(false);
      return;
    }
    try {
      // Build plan_pricings array with same structure as edit pricing
      const planPricings = pricingList.map((pricing) => {
        const countryId = getCountryId(pricing.country);
        const denominationId = getDenominationId(pricing.currency);

        if (!countryId) {
          throw new Error(`Invalid country: ${pricing.country}`);
        }
        if (!denominationId) {
          throw new Error(`Invalid currency: ${pricing.currency}`);
        }

        return {
          operation: "ADD",
          plan_id: planId,
          plan_duration_days: Number(pricing.duration) * 30,
          price: Number(pricing.price),
          max_discount_percent: Number(pricing.max_discount),
          country_id: countryId,
          denomination_id: denominationId,
        };
      });

      // Build payload with same structure as edit pricing
      const payload = {
        event_type_id: "4850387a-8838-45d5-8c65-dce9d1f86de6",
        plan_pricings: planPricings,
      };

      await createPricingPlan(post, payload);

      setToastMessage("Pricing added successfully!" );
      setToastType("success");
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1200);
    } catch (err) {
      setToastMessage( err?.response?.data?.detail?.[0]?.msg || err?.message || "Failed to add pricing.");
      setToastType("error");
    } finally {
      setLoading(false);
    }
  };

  if (loadingUtils) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-600">Loading configuration...</div>
      </div>
    );
  }

  // Get available countries and currencies from commonUtils
  const countryOptions = commonUtils?.countries?.map((c) => c.name) || [];
  const currencyOptions = commonUtils?.denominations?.map((d) => d.name) || [];

  return (
    <form className="flex flex-col gap-4" onSubmit={handlePricingSubmit}>
      <div className="text-lg font-bold mb-1">
        {headerText}
        {planName && (
          <span className="ml-2 font-mono text-orange-700">({planName})</span>
        )}
      </div>
      <p className="text-sm text-gray-600 -mt-1">
        Add one or more pricing options. Each option is a unique country + duration combination.
      </p>
      {pricingList.map((pricing, idx) => (
        <div
          key={idx}
          className="flex gap-3 items-end border border-gray-200 rounded-lg p-3 flex-wrap bg-gray-50"
        >
          <div className="w-full flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">
              Option {idx + 1}
            </p>
            {pricingList.length > 1 && (
              <button
                type="button"
                onClick={() => removePricingOption(idx)}
                className="text-red-600 text-xs font-semibold hover:text-red-700"
                title="Remove pricing option"
              >
                Remove
              </button>
            )}
          </div>
          {/* Duration */}
          <div className="flex-1">
            <label className="text-sm block mb-1 font-medium">Duration (months)</label>
            <input type="number" min={1} className="border px-2 py-2 rounded w-full"
              value={pricing.duration}
              onChange={e => handlePricingChange(idx, "duration", e.target.value)}
              required
            />
          </div>
          {/* Price */}
          <div className="flex-1">
            <label className="text-sm block mb-1 font-medium">Price</label>
            <input type="number" min={0} className="border px-2 py-2 rounded w-full"
              value={pricing.price}
              onChange={e => handlePricingChange(idx, "price", e.target.value)}
              required
            />
          </div>
          {/* Max Discount */}
          <div className="flex-1">
            <label className="text-sm block mb-1 font-medium">Max Discount (%)</label>
            <input type="number" min={0} max={100} className="border px-2 py-2 rounded w-full"
              value={pricing.max_discount}
              onChange={e => handlePricingChange(idx, "max_discount", e.target.value)}
              required
            />
          </div>
          {/* Country */}
          <div className="flex-1">
            <label className="text-sm block mb-1 font-medium">Country</label>
            <select className="border px-2 py-2 rounded w-full"
              value={pricing.country}
              onChange={e => handlePricingChange(idx, "country", e.target.value)}
              required
            >
              <option value="">Select a country</option>
              {countryOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          {/* Currency */}
          <div className="flex-1">
            <label className="text-sm block mb-1 font-medium">Currency</label>
            <select className="border px-2 py-2 rounded w-full"
              value={pricing.currency}
              onChange={e => handlePricingChange(idx, "currency", e.target.value)}
              required
            >
              <option value="">Select a currency</option>
              {currencyOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="text-orange-700 mt-1 font-semibold text-sm self-start hover:text-orange-800"
        onClick={addPricingOption}
      >
        + Add Another Pricing Option
      </button>
      <div className="flex gap-3 mt-2">
        <button
          type="submit"
          disabled={loading}
          className="bg-orange-primary text-white font-semibold px-5 py-2 rounded shadow hover:bg-orange-700 disabled:opacity-50"
        >
          {loading ? "Saving..." : submitLabel}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="bg-gray-200 text-gray-700 px-5 py-2 rounded shadow hover:bg-gray-300"
          disabled={loading}
        >
          Cancel
        </button>
      </div>
      {toastMessage && <Toaster message={toastMessage} type={toastType} />}
    </form>
  );
};

export default PricingListForm;
